import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	Observable,
	Subject,
	Subscription,
	firstValueFrom,
	takeUntil,
} from 'rxjs';
import { MAX_TILES_PER_BATCH } from './orthophoto.model';
import { OrthophotoApi } from './orthophoto-api';
import { ServiceNotification } from '@shared/services/notification/service-notification';
import { runWithConcurrency } from '@shared/utils/concurrency-pool';
import { DroppedFile } from '@shared/utils/directory-entries';
import { TileFile, chunk, toTileFiles } from '@shared/utils/tile-path';

export type OrthophotoUploadStatus =
	| 'idle'
	| 'uploading'
	| 'processing'
	| 'done'
	| 'error'
	| 'cancelled';

export interface OrthophotoUploadState {
	status: OrthophotoUploadStatus;
	progress: number;
	totalTiles: number;
	completedTiles: number;
	phaseLabel: string;
	folderName?: string;
	errorMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class OrthophotoUploadManagerService {
	private readonly toastKey = 'orthophoto-upload';

	private readonly TILE_UPLOAD_CONCURRENCY = 8;

	private readonly stateSubject = new BehaviorSubject<OrthophotoUploadState>({
		status: 'idle',
		progress: 0,
		totalTiles: 0,
		completedTiles: 0,
		phaseLabel: '',
	});
	readonly state$ = this.stateSubject.asObservable();

	private cancelled = false;
	private cancelSignal$ = new Subject<void>();
	private activeSubs = new Set<Subscription>();
	private isSubmitting = false;

	constructor(
		private orthophotoService: OrthophotoApi,
		private notification: ServiceNotification,
	) {}

	getState(): OrthophotoUploadState {
		return this.stateSubject.value;
	}

	isBusy(): boolean {
		const s = this.getState().status;
		return s === 'uploading' || s === 'processing';
	}

	async startUpload(
		farmId: string,
		dropped: DroppedFile[],
		onSuccess?: () => void,
	): Promise<void> {
		if (this.isSubmitting) return;

		const tiles = toTileFiles(dropped);
		const folderName = dropped[0]?.relativePath.split('/')[0];

		if (tiles.length === 0) {
			this.notification.error(
				'Pasta inválida',
				'Nenhum tile encontrado. A pasta deve conter a pirâmide no formato {zoom}/{x}/{y}.png, com zoom entre 14 e 20.',
			);
			return;
		}

		this.isSubmitting = true;
		this.cancelled = false;
		this.cancelSignal$.complete();
		this.cancelSignal$ = new Subject<void>();
		this.cleanupSubs();

		this.setState({
			status: 'uploading',
			progress: 0,
			totalTiles: tiles.length,
			completedTiles: 0,
			phaseLabel: 'Validando fazenda…',
			folderName,
			errorMessage: undefined,
		});
		this.showToast();

		try {
			const { uploadId } = await this.request(
				this.orthophotoService.initiateTilesUpload(farmId),
			);

			let completedTiles = 0;
			const batches = chunk(tiles, MAX_TILES_PER_BATCH);

			for (const batch of batches) {
				if (this.cancelled) throw new Error('cancelled');

				this.setState({
					phaseLabel: `Enviando tiles (${completedTiles}/${tiles.length})…`,
				});

				const { tiles: signed } = await this.request(
					this.orthophotoService.signTiles(
						farmId,
						uploadId,
						batch.map((t) => t.tilePath),
					),
				);

				const urlByTilePath = new Map(
					signed.map((s) => [s.tilePath, s.url] as const),
				);

				await runWithConcurrency(
					batch,
					this.TILE_UPLOAD_CONCURRENCY,
					(tile) => {
						if (this.cancelled) throw new Error('cancelled');
						const url = urlByTilePath.get(tile.tilePath);
						if (!url) {
							throw new Error(
								`URL assinada não retornada para ${tile.tilePath}`,
							);
						}
						return this.uploadTileWithRetry(farmId, uploadId, tile, url);
					},
					() => {
						completedTiles++;
						this.setState({
							completedTiles,
							progress: Math.min(
								99,
								Math.round((completedTiles / tiles.length) * 100),
							),
						});
					},
				);
			}

			if (this.cancelled) throw new Error('cancelled');

			this.setState({
				status: 'processing',
				phaseLabel: 'Publicando ortofoto…',
			});

			await this.request(
				this.orthophotoService.finalizeTilesUpload(
					farmId,
					uploadId,
					tiles.length,
				),
			);

			this.setState({
				status: 'done',
				progress: 100,
				phaseLabel: 'Concluído',
				completedTiles: tiles.length,
			});
			this.notification.success(
				'Sucesso',
				`Ortofoto cadastrada (${tiles.length} tiles).`,
			);
			onSuccess?.();

			setTimeout(() => this.reset(), 2000);
		} catch (err: any) {
			if (this.cancelled || err?.message === 'cancelled') {
				this.setState({
					status: 'cancelled',
					progress: 0,
					phaseLabel: '',
					errorMessage: undefined,
				});
				this.notification.warn(
					'Upload cancelado',
					'Envio da ortofoto cancelado.',
				);
				this.clearToast();
				return;
			}
			this.fail(err);
		} finally {
			this.isSubmitting = false;
			this.cleanupSubs();
		}
	}

	private async uploadTileWithRetry(
		farmId: string,
		uploadId: string,
		tile: TileFile,
		url: string,
		maxRetries = 3,
	): Promise<void> {
		let currentUrl = url;

		for (let attempt = 0; ; attempt++) {
			try {
				await this.request(
					this.orthophotoService.uploadTile(currentUrl, tile.file),
				);
				return;
			} catch (err: any) {
				if (this.cancelled || err?.message === 'cancelled') throw err;

				const isNetworkError = err?.status === 0;
				if (attempt >= maxRetries - 1 || !isNetworkError) throw err;

				await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));

				const { tiles: resigned } = await this.request(
					this.orthophotoService.signTiles(farmId, uploadId, [tile.tilePath]),
				);
				currentUrl = resigned[0]?.url ?? currentUrl;
			}
		}
	}

	private request<T>(obs: Observable<T>): Promise<T> {
		return firstValueFrom(obs.pipe(takeUntil(this.cancelSignal$)));
	}

	cancel(): void {
		this.isSubmitting = false;
		this.cancelled = true;
		this.cancelSignal$.next();
		this.cancelSignal$.complete();
		this.cancelSignal$ = new Subject<void>();
		this.cleanupSubs();
		this.setState({
			status: 'cancelled',
			progress: 0,
			phaseLabel: '',
			errorMessage: undefined,
		});
		this.notification.warn('Upload cancelado', 'Envio da ortofoto cancelado.');
		this.clearToast();
	}

	private fail(err: any): void {
		const msg =
			err?.error?.message ||
			(typeof err?.error === 'string' ? err.error : null) ||
			err?.message ||
			'Não foi possível fazer o upload da ortofoto.';
		this.setState({
			status: 'error',
			errorMessage: msg,
			phaseLabel: 'Erro',
		});
		this.notification.error('Erro', msg);
		setTimeout(() => this.clearToast(), 3000);
	}

	private reset(): void {
		this.clearToast();
		this.setState({
			status: 'idle',
			progress: 0,
			totalTiles: 0,
			completedTiles: 0,
			phaseLabel: '',
			folderName: undefined,
		});
	}

	private setState(patch: Partial<OrthophotoUploadState>): void {
		this.stateSubject.next({ ...this.stateSubject.value, ...patch });
	}

	private showToast(): void {
		this.notification.showProgress(this.toastKey, 'Upload de ortofoto', {
			data: { type: 'orthophoto-upload-progress' },
		});
	}

	private clearToast(): void {
		this.notification.clearProgress(this.toastKey);
	}

	private cleanupSubs(): void {
		for (const s of this.activeSubs) s.unsubscribe();
		this.activeSubs.clear();
	}
}
